import PropTypes from 'prop-types';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import expandFill from '@iconify/icons-eva/expand-fill';
import collapseFill from '@iconify/icons-eva/collapse-fill';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, Typography } from '@material-ui/core';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import { MHidden } from '../../components/@material-extend';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import FacilitySelector, { FACILITIES } from './FacilitySelector';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 88;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;
const DARK_MODE_GREEN = '#85e966';

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isDarkModeFacility' && prop !== 'isSidebarOpen'
})(({ theme, isDarkModeFacility, isSidebarOpen }) => ({
  boxShadow: 'none',
  ...(isDarkModeFacility
    ? {
        backgroundColor: DARK_MODE_GREEN,
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none'
      }
    : {
        backgroundColor: alpha(theme.palette.background.default, 0.72),
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)'
      }),
  [theme.breakpoints.up('lg')]: {
    width: isSidebarOpen ? `calc(100% - ${DRAWER_WIDTH + (isDarkModeFacility ? 0 : 1)}px)` : `calc(100% - ${COLLAPSED_DRAWER_WIDTH}px)`,
    marginLeft: isSidebarOpen ? `${DRAWER_WIDTH + (isDarkModeFacility ? 0 : 1)}px` : `${COLLAPSED_DRAWER_WIDTH}px`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  height: APPBAR_MOBILE,
  minWidth: 0,
  overflow: 'hidden',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  gap: theme.spacing(0.5),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
    gap: 0
  }
}));

// ----------------------------------------------------------------------

DashboardNavbar.propTypes = {
  isSidebarOpen: PropTypes.bool,
  onOpenSidebar: PropTypes.func
};

export const isSuperAdminOrAdmin = (user) => {
  if (!user) return false;

  if (user.roleId === 1 || user.roleId === 2) return true;

  const roleStr = String(user.role || '').trim().toLowerCase();
  if (
    roleStr === 'super administrator' ||
    roleStr === 'super admin' ||
    roleStr === 'administrator' ||
    roleStr === 'admin'
  ) {
    return true;
  }

  if (Array.isArray(user.roles)) {
    return user.roles.some((r) => {
      const rStr = String(r.role || r.role_name || r || '').trim().toLowerCase();
      const rId = r.roleId || r.role_id || r.id;
      return (
        rId === 1 ||
        rId === 2 ||
        rStr === 'super administrator' ||
        rStr === 'super admin' ||
        rStr === 'administrator' ||
        rStr === 'admin'
      );
    });
  }

  return false;
};

export default function DashboardNavbar({ isSidebarOpen, onOpenSidebar }) {
  const { user, selectedFacility } = useAuth();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const isAllowedUser = isSuperAdminOrAdmin(user);

  const selectedFacilityObj = FACILITIES.find(f => f.id === selectedFacility);
  const isDarkModeFacility = selectedFacilityObj?.name?.toLowerCase().includes('dark mode') || false;
  const iconColor = isDarkModeFacility ? 'common.black' : 'text.primary';

  const handleToggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  return (
    <RootStyle isDarkModeFacility={isDarkModeFacility} isSidebarOpen={isSidebarOpen}>
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} sx={{ mr: { xs: 0, sm: 1 }, p: { xs: 0.5, sm: 1 }, color: iconColor, flexShrink: 0 }}>
            <Icon icon={menu2Fill} width={20} height={20} />
          </IconButton>
        </MHidden>

        <Searchbar isSidebarOpen={isSidebarOpen} isDarkModeFacility={isDarkModeFacility} />
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', minWidth: 0, px: { xs: 0.5, sm: 0 } }}>
          <Typography
            variant="h6"
            noWrap
            sx={{
              color: iconColor,
              fontFamily: '"El Messiri", "Reem Kufi", "Traditional Arabic", serif',
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.65rem' },
              letterSpacing: '0.08em',
              lineHeight: 1.35,
              textTransform: 'none'
            }}
          >
            {selectedFacilityObj?.name || ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={{ xs: 0, sm: 1.5 }} alignItems="center" sx={{ color: iconColor, flexShrink: 0 }}>
          {isAllowedUser && <FacilitySelector isDarkModeFacility={isDarkModeFacility} />}
          
          <IconButton 
            onClick={handleToggleFullScreen}
            sx={{ 
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              p: { xs: 0.5, sm: 1 },
              color: iconColor 
            }}
          >
            <Icon icon={isFullScreen ? collapseFill : expandFill} width={18} height={18} />
          </IconButton>

          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}